import React, {useEffect, useMemo, useState} from 'react';
import {
    BarChart3,
    Building2,
    DollarSign,
    Package,
    TrendingUp,
    Users
} from 'lucide-react';
import {useNavigate} from 'react-router-dom';
import {PageHeader} from '../../shared/ui';
import {
    Alert,
    Box,
    CircularProgress,
    Paper,
    Stack,
    Typography,
    useTheme,
} from '@mui/material';
import {getEmpresasFiltered} from './EmpresaService';
import {getBackendWarningMessage} from '../../shared/utils';
import {getEmpresasMenus} from './empresasMenu';

const getPaginatedResult = (response) => {
    const value = response?.data?.valor || response?.data || {};
    const items = value.items || [];
    const pagination = value.pagination || value.meta || {};
    return {
        items,
        total: pagination.total || items.length || 0,
    };
};

const getStatus = (empresa) => empresa?.status || (empresa?.ativo === false ? 'INATIVO' : 'ATIVO');

const formatMoney = (value) => new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
}).format(Number(value) || 0);

export default function EmpresasDashboardPage({openParent = true}) {
    const theme = useTheme();
    const navigate = useNavigate();
    const [open, setOpen] = useState(openParent);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        let mounted = true;

        const load = async () => {
            try {
                setLoading(true);
                const response = await getEmpresasFiltered({
                    page: 1,
                    pageSize: 1000,
                    filter: '',
                    sortBy: 'razao_social',
                    order: 'asc',
                });
                const result = getPaginatedResult(response);
                if (!mounted) return;
                setRows(result.items);
            } catch (requestError) {
                if (!mounted) return;
                setError(getBackendWarningMessage(requestError) || 'Falha ao carregar métricas de clientes.');
            } finally {
                if (mounted) setLoading(false);
            }
        };

        load();
        return () => {
            mounted = false;
        };
    }, []);

    const metrics = useMemo(() => {
        const totalClientes = rows.length;
        const clientesAtivos = rows.filter((item) => getStatus(item) === 'ATIVO').length;
        const mrr = rows.reduce((acc, item) => acc + (Number(item?.valor_mensal) || 0), 0);
        const assinaturasTotais = rows.reduce((acc, item) => acc + (Number(item?.assinaturas_ativas) || 0), 0) || clientesAtivos;

        const now = new Date();
        const novosClientesMes = rows.filter((item) => {
            const rawDate = item?.data_cadastro || item?.createdAt;
            if (!rawDate) return false;
            const date = new Date(rawDate);
            if (Number.isNaN(date.getTime())) return false;
            return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
        }).length;

        const crescimentoMrr = 0;

        return {
            totalClientes,
            clientesAtivos,
            mrr,
            assinaturasTotais,
            novosClientesMes,
            crescimentoMrr,
        };
    }, [rows]);

    const statusDistribution = useMemo(() => {
        const result = {ATIVO: 0, INATIVO: 0, BLOQUEADO: 0};
        rows.forEach((item) => {
            const status = getStatus(item);
            if (status === 'ATIVO' || status === 'INATIVO' || status === 'BLOQUEADO') {
                result[status] += 1;
            }
        });
        return result;
    }, [rows]);

    const cards = [
        {
            title: 'Total de Clientes',
            value: metrics.totalClientes,
            helper: `+${metrics.novosClientesMes} este mês`,
            icon: Building2,
            iconBg: '#ccfbf1',
            iconColor: '#0f766e',
            helperColor: '#15803d',
        },
        {
            title: 'Clientes Ativos',
            value: metrics.clientesAtivos,
            helper: `${metrics.totalClientes ? ((metrics.clientesAtivos / metrics.totalClientes) * 100).toFixed(1) : '0.0'}% do total`,
            icon: TrendingUp,
            iconBg: '#dcfce7',
            iconColor: '#15803d',
            helperColor: '#6b7280',
        },
        {
            title: 'MRR (Receita Mensal)',
            value: formatMoney(metrics.mrr),
            helper: `+${metrics.crescimentoMrr}% vs mês anterior`,
            icon: DollarSign,
            iconBg: '#dbeafe',
            iconColor: '#1d4ed8',
            helperColor: '#15803d',
        },
        {
            title: 'Assinaturas Ativas',
            value: metrics.assinaturasTotais,
            helper: `~${metrics.clientesAtivos ? (metrics.assinaturasTotais / metrics.clientesAtivos).toFixed(1) : '0.0'} por cliente`,
            icon: Package,
            iconBg: '#f3e8ff',
            iconColor: '#7e22ce',
            helperColor: '#6b7280',
        },
    ];

    return (
        <Box sx={{display: 'flex', minHeight: '100dvh'}}>
            <PageHeader
                title="CRM"
                titleAction={() => navigate('/')}
                itemsMenu={getEmpresasMenus(navigate, 'dashboard')}
                onHandleMenuOpen={() => null}
                onHandleDrawerToggle={setOpen}
                openParent={open}
            />
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: {xs: 2, md: 6},
                    minWidth: 0,
                    boxSizing: 'border-box',
                    transition: theme.transitions.create(['margin', 'width']),
                    mt: 8,
                    backgroundColor: theme.palette.background.default,
                    minHeight: 'calc(100dvh - 64px)',
                }}
            >
                <Stack spacing={3}>
                    <Box>
                        <Typography variant="h4" fontWeight={700}>Dashboard CRM</Typography>
                        <Typography color="text.secondary">
                            Métricas executivas e visão consolidada do relacionamento com empresas.
                        </Typography>
                    </Box>

                    {error ? <Alert severity="error">{error}</Alert> : null}

                    <Box sx={{display: 'grid', gap: 2, gridTemplateColumns: {xs: '1fr', md: 'repeat(2, 1fr)', xl: 'repeat(4, 1fr)'}}}>
                        {cards.map((card) => {
                            const Icon = card.icon;
                            return (
                                <Paper key={card.title} variant="outlined" sx={{p: 2.5, borderRadius: 2.5}}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{mb: 1.5}}>
                                        <Box sx={{width: 46, height: 46, borderRadius: 2, bgcolor: card.iconBg, color: card.iconColor, display: 'grid', placeItems: 'center'}}>
                                            <Icon size={22} />
                                        </Box>
                                    </Stack>
                                    <Typography variant="h5" fontWeight={700}>{card.value}</Typography>
                                    <Typography variant="body2" color="text.secondary">{card.title}</Typography>
                                    <Typography variant="caption" sx={{color: card.helperColor}}>{card.helper}</Typography>
                                </Paper>
                            );
                        })}
                    </Box>

                    <Box sx={{display: 'grid', gap: 2, gridTemplateColumns: {xs: '1fr', lg: '2fr 1fr'}}}>
                        <Paper variant="outlined" sx={{p: 2.5, borderRadius: 2.5, minHeight: 320}}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{mb: 2}}>
                                <Typography variant="h6" fontWeight={700}>Evolução Comercial</Typography>
                                <Typography variant="body2" color="text.secondary">Últimos 30 dias</Typography>
                            </Stack>
                            {loading ? (
                                <Box sx={{display: 'grid', placeItems: 'center', height: 220}}>
                                    <CircularProgress size={22} />
                                </Box>
                            ) : (
                                <Box sx={{height: 220, borderRadius: 2, bgcolor: '#f8fafc', border: '1px dashed #cbd5e1', display: 'grid', placeItems: 'center'}}>
                                    <Stack spacing={1} alignItems="center">
                                        <BarChart3 size={24} color="#64748b" />
                                        <Typography color="text.secondary">Área reservada para gráfico de evolução</Typography>
                                    </Stack>
                                </Box>
                            )}
                        </Paper>

                        <Paper variant="outlined" sx={{p: 2.5, borderRadius: 2.5, minHeight: 320}}>
                            <Typography variant="h6" fontWeight={700} sx={{mb: 2}}>Status dos Clientes</Typography>
                            <Stack spacing={1.5}>
                                <Box sx={{display: 'flex', justifyContent: 'space-between'}}>
                                    <Typography color="text.secondary">Ativos</Typography>
                                    <Typography fontWeight={700} sx={{color: '#15803d'}}>{statusDistribution.ATIVO}</Typography>
                                </Box>
                                <Box sx={{display: 'flex', justifyContent: 'space-between'}}>
                                    <Typography color="text.secondary">Inativos</Typography>
                                    <Typography fontWeight={700} sx={{color: '#374151'}}>{statusDistribution.INATIVO}</Typography>
                                </Box>
                                <Box sx={{display: 'flex', justifyContent: 'space-between'}}>
                                    <Typography color="text.secondary">Bloqueados</Typography>
                                    <Typography fontWeight={700} sx={{color: '#b91c1c'}}>{statusDistribution.BLOQUEADO}</Typography>
                                </Box>
                                <Box sx={{display: 'flex', justifyContent: 'space-between'}}>
                                    <Typography color="text.secondary">Total</Typography>
                                    <Typography fontWeight={700}>{metrics.totalClientes}</Typography>
                                </Box>
                            </Stack>
                            <Box sx={{mt: 2.5, p: 1.5, borderRadius: 2, bgcolor: '#eef2ff'}}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Users size={16} color="#4338ca" />
                                    <Typography variant="body2" sx={{color: '#4338ca'}}>
                                        Dados consolidados da base de clientes.
                                    </Typography>
                                </Stack>
                            </Box>
                        </Paper>
                    </Box>
                </Stack>
            </Box>
        </Box>
    );
}
